import { useEffect, useMemo, useRef } from 'react'

import { QuillStatic } from '../../types'

import { DictionaryHighlightOptions, DictionaryHighlightProps } from './types'
import {
  DICTIONARY_HIGHLIGHT_BLOT_NAME,
  DICTIONARY_HIGHLIGHT_MODULE_NAME,
} from './constants'
import { registerDictionaryHighlightModule } from './module'

export const registerDictionaryHighlight = (Quill: QuillStatic) => {
  registerDictionaryHighlightModule(Quill)
}

export const useDictionaryHighlightModules = (
  modules: {
    [key: string]: any
  } = {},
  props: DictionaryHighlightProps = {},
) => {
  const isActivatedDictionaryHighlight = !!props.dictionaryHighlight

  const getDictionaryRef = useRef<DictionaryHighlightOptions['getDictionary']>()

  const getSelectedWordRef =
    useRef<DictionaryHighlightOptions['getSelectedWord']>()

  const onClickRegisteredWordRef =
    useRef<DictionaryHighlightOptions['onClickRegisteredWord']>()

  useEffect(() => {
    if (
      !!props.dictionaryHighlight &&
      typeof props.dictionaryHighlight === 'object'
    ) {
      getDictionaryRef.current = props.dictionaryHighlight?.getDictionary
      getSelectedWordRef.current = props.dictionaryHighlight?.getSelectedWord
      onClickRegisteredWordRef.current =
        props.dictionaryHighlight?.onClickRegisteredWord
    } else {
      getDictionaryRef.current = undefined
      getSelectedWordRef.current = undefined
      onClickRegisteredWordRef.current = undefined
    }
  }, [props.dictionaryHighlight])

  const memoizedModules = useMemo(() => {
    const mergedModules = {
      ...modules,
    }

    if (isActivatedDictionaryHighlight) {
      mergedModules[DICTIONARY_HIGHLIGHT_MODULE_NAME] = {
        getDictionary: () => getDictionaryRef.current?.(),
        getSelectedWord: () => getSelectedWordRef.current?.(),
        onClickRegisteredWord: (e: MouseEvent, word: string, rect: DOMRect) =>
          onClickRegisteredWordRef.current?.(e, word, rect),
      }
    }

    return mergedModules
  }, [modules, isActivatedDictionaryHighlight])

  return memoizedModules
}

export const useDictionaryHighlightFormats = (
  formats: string[] = [],
  props: DictionaryHighlightProps = {},
) => {
  const isActivatedDictionaryHighlight = !!props.dictionaryHighlight

  const memoizedFormats = useMemo(() => {
    const mergedFormats = [...formats]

    if (isActivatedDictionaryHighlight) {
      mergedFormats.push(DICTIONARY_HIGHLIGHT_BLOT_NAME)
    }

    return mergedFormats
  }, [formats, isActivatedDictionaryHighlight])

  return memoizedFormats
}
